import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";

import { api, ApiError } from "../api/client";
import type { ApiKeyCreatedOut } from "../api/types";
import { useAuth } from "../hooks/useAuth";
import { useDocumentTitle } from "../hooks/useDocumentTitle";

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString();
}

/** The raw key, shown exactly once after creation. */
function NewKeyBanner({ created }: { created: ApiKeyCreatedOut }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3 dark:border-amber-700 dark:bg-amber-950">
      <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
        Copy your new key now — it won't be shown again.
      </p>
      <div className="mt-2 flex items-center gap-2">
        <code className="min-w-0 flex-1 break-all rounded bg-white px-2 py-1 text-xs dark:bg-slate-900">
          {created.key}
        </code>
        <button
          type="button"
          className="btn-secondary shrink-0"
          onClick={() => {
            navigator.clipboard.writeText(created.key).then(() => setCopied(true));
          }}
        >
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
    </div>
  );
}

function KeysCard() {
  const queryClient = useQueryClient();
  const keys = useQuery({ queryKey: ["keys"], queryFn: api.listKeys });
  const [name, setName] = useState("");
  const [lastCreated, setLastCreated] = useState<ApiKeyCreatedOut | null>(null);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["keys"] });
    queryClient.invalidateQueries({ queryKey: ["usage"] });
  };
  const create = useMutation({
    mutationFn: () => api.createKey(name.trim() || undefined),
    onSuccess: (created) => {
      setLastCreated(created);
      setName("");
      invalidate();
    },
  });
  const revoke = useMutation({
    mutationFn: (id: number) => api.revokeKey(id),
    onSuccess: invalidate,
  });

  const createError =
    create.error instanceof ApiError && create.error.status === 403
      ? "Verify your email first — check your inbox for the verification link."
      : create.error instanceof ApiError && create.error.status === 400
        ? "Active key limit reached; revoke one first."
        : create.isError
          ? "Could not create the key. Please try again."
          : null;

  const active = (keys.data ?? []).filter((k) => !k.revoked_at);
  const revoked = (keys.data ?? []).filter((k) => k.revoked_at);

  return (
    <div className="card">
      <h2 className="text-lg font-semibold">API keys</h2>
      <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
        Send as <code>X-API-Key</code> or <code>Authorization: Bearer</code>. See{" "}
        <Link to="/api-docs" className="text-sky-700 underline dark:text-sky-400">
          Data &amp; API
        </Link>{" "}
        for usage limits.
      </p>

      {lastCreated && <NewKeyBanner created={lastCreated} />}

      <form
        className="mt-4 flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate();
        }}
      >
        <input
          type="text"
          maxLength={64}
          placeholder="Key name (optional, e.g. “ingest job”)"
          className="min-w-0 flex-1 rounded-md border border-slate-300 px-3 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-900"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <button type="submit" disabled={create.isPending} className="btn-primary shrink-0">
          {create.isPending ? "Creating…" : "Create key"}
        </button>
      </form>
      {createError && (
        <p className="mt-2 text-sm text-red-600 dark:text-red-400">{createError}</p>
      )}

      {keys.isLoading ? (
        <p className="mt-4 text-sm text-slate-500 dark:text-slate-400">Loading…</p>
      ) : active.length === 0 ? (
        <p className="mt-4 text-sm text-slate-500 dark:text-slate-400">No active keys yet.</p>
      ) : (
        <table className="mt-4 w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
              <th className="py-1 pr-2">Key</th>
              <th className="py-1 pr-2">Name</th>
              <th className="py-1 pr-2">Created</th>
              <th className="py-1 pr-2">Last used</th>
              <th className="py-1" />
            </tr>
          </thead>
          <tbody>
            {active.map((k) => (
              <tr key={k.id} className="border-t border-slate-200 dark:border-slate-800">
                <td className="py-2 pr-2">
                  <code>{k.prefix}…</code>
                </td>
                <td className="py-2 pr-2">{k.name || "—"}</td>
                <td className="py-2 pr-2">{fmtDate(k.created_at)}</td>
                <td className="py-2 pr-2">{fmtDate(k.last_used_at)}</td>
                <td className="py-2 text-right">
                  <button
                    type="button"
                    className="text-sm font-medium text-red-600 hover:underline dark:text-red-400"
                    disabled={revoke.isPending}
                    onClick={() => {
                      if (window.confirm(`Revoke key ${k.prefix}…? This cannot be undone.`)) {
                        revoke.mutate(k.id);
                      }
                    }}
                  >
                    Revoke
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {revoked.length > 0 && (
        <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">
          {revoked.length} revoked key{revoked.length > 1 ? "s" : ""} hidden.
        </p>
      )}
    </div>
  );
}

function UsageCard() {
  const usage = useQuery({ queryKey: ["usage"], queryFn: api.usage });
  if (usage.isLoading || !usage.data) return null;
  const u = usage.data;
  const pct =
    u.daily_limit && u.daily_limit > 0
      ? Math.min(100, Math.round((u.today / u.daily_limit) * 100))
      : 0;
  return (
    <div className="card">
      <h2 className="text-lg font-semibold">Usage today</h2>
      <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
        {u.today.toLocaleString()} requests
        {u.daily_limit ? ` of ${u.daily_limit.toLocaleString()} daily` : ""}
        {u.per_minute_limit ? ` · ${u.per_minute_limit}/min burst` : ""}
      </p>
      {u.daily_limit ? (
        <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
          <div
            className={`h-full ${pct >= 90 ? "bg-red-500" : pct >= 70 ? "bg-amber-500" : "bg-sky-600"}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      ) : null}
      {u.keys.length > 1 && (
        <ul className="mt-3 space-y-1 text-sm text-slate-600 dark:text-slate-400">
          {u.keys.map((k) => (
            <li key={k.prefix}>
              <code>{k.prefix}…</code> {k.name ? `(${k.name})` : ""} —{" "}
              {k.today.toLocaleString()} today
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** The account holder's email alerts, listed by the same endpoint /alerts uses.
 *  Editing happens on /alerts, so this stays a summary with a way in. */
function AlertsCard() {
  const subs = useQuery({
    queryKey: ["subscriptions", "session"],
    queryFn: () => api.listSubscriptions(),
    retry: false,
  });

  const unverified = subs.error instanceof ApiError && subs.error.status === 401;
  const rows = subs.data ?? [];

  return (
    <div className="card">
      <h2 className="text-lg font-semibold">Email alerts</h2>
      <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
        Saved searches we email you about. Alerts you created with this address
        before signing up show up here too.
      </p>

      {subs.isLoading ? (
        <p className="mt-4 text-sm text-slate-500 dark:text-slate-400">Loading…</p>
      ) : unverified ? (
        <p className="mt-4 text-sm text-amber-700 dark:text-amber-400">
          Verify your email first — check your inbox for the verification link.
        </p>
      ) : subs.isError ? (
        <p className="mt-4 text-sm text-red-600 dark:text-red-400">
          Could not load your alerts. Please try again later.
        </p>
      ) : rows.length === 0 ? (
        <p className="mt-4 text-sm text-slate-500 dark:text-slate-400">No alerts yet.</p>
      ) : (
        <ul className="mt-4 space-y-2 text-sm">
          {rows.map((s) => (
            <li key={s.id} className="flex flex-wrap items-baseline justify-between gap-2">
              <span>{s.scope}</span>
              <span className="text-xs text-slate-500 dark:text-slate-400">
                {s.frequency === "weekly" ? "Weekly" : "Daily"} ·{" "}
                {s.confirmed ? "Active" : "Awaiting confirmation"}
              </span>
            </li>
          ))}
        </ul>
      )}

      <p className="mt-4 text-sm">
        <Link to="/alerts" className="text-sky-700 underline dark:text-sky-400">
          Manage alerts
        </Link>
      </p>
    </div>
  );
}

export function AccountPage() {
  useDocumentTitle("Account — WARN Index");
  const auth = useAuth();

  if (auth.isLoading) return null;
  if (!auth.data) {
    return (
      <div className="card mx-auto max-w-md text-center">
        <h1 className="text-xl font-semibold">Account</h1>
        <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
          <Link to="/login" className="text-sky-700 underline dark:text-sky-400">
            Sign in
          </Link>{" "}
          or{" "}
          <Link to="/signup" className="text-sky-700 underline dark:text-sky-400">
            create an account
          </Link>{" "}
          to manage API keys.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <h1 className="text-2xl font-semibold">Account</h1>
      <p className="text-sm text-slate-500 dark:text-slate-400">{auth.data.email}</p>
      <AlertsCard />
      <KeysCard />
      <UsageCard />
    </div>
  );
}
